
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useDatabase } from '@/context/DatabaseContext';
import { AssessmentResponse, AssessmentResult } from '@/types/assessment';
import { toast } from 'sonner';

export const useAssessmentSubmission = (
  questions: any[],
  responses: Record<number, string>,
  canEarlySubmit: boolean,
  activeUserId: string | null,
  currentOrderId: string | null
) => {
  const navigate = useNavigate();
  const { saveResponses, generateReport } = useDatabase();
  const { toast: uiToast } = useToast();
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitError(null);

    const userId = activeUserId || 'anonymous';
    console.log('Submitting assessment with user ID:', userId);
    
    const requiredQuestions = canEarlySubmit 
      ? questions.filter(q => q.id <= 63) // STUDY_ABROAD_QUESTION_ID = 63
      : questions;
    
    const unansweredQuestions = requiredQuestions.filter(q => !responses[q.id]);
    
    if (unansweredQuestions.length > 0) {
      setSubmitError(`You have ${unansweredQuestions.length} unanswered questions. Please complete all questions before submitting.`);
      toast.error(`You have ${unansweredQuestions.length} unanswered questions. Please complete all questions before submitting.`);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const formattedResponses: AssessmentResponse[] = Object.entries(responses).map(([questionId, answer]) => ({
        questionId: parseInt(questionId),
        answer,
        orderId: currentOrderId || undefined
      }));
      
      localStorage.setItem(`responses_${userId}`, JSON.stringify(formattedResponses));
      
      await saveResponses(userId, formattedResponses);
      
      setIsSubmitting(false);
      setIsGeneratingReport(true);
      
      const result = await generateReport(userId, formattedResponses);
      console.log('Report generated:', result);
      
      localStorage.setItem(`report_${userId}`, JSON.stringify(result));
      
      localStorage.removeItem('preferred_careers');
      
      setIsGeneratingReport(false);
      setShowSuccessMessage(true);
      
      setTimeout(() => {
        const orderIdParam = currentOrderId ? `?order=${currentOrderId}` : '';
        navigate(`/results${orderIdParam}`);
        
        localStorage.removeItem('current_order_id');
      }, 3000);
      
    } catch (error) {
      console.error('Error submitting assessment:', error);
      setIsSubmitting(false);
      setIsGeneratingReport(false);
      
      let errorMessage = "There was an error submitting your assessment. Please try again later.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setSubmitError(errorMessage);
      
      toast.error(errorMessage);
    }
  };

  return {
    isSubmitting,
    isGeneratingReport,
    showSuccessMessage,
    submitError,
    handleSubmit
  };
};
